/**
 * Applicera färgschema och label-bakgrund från form-selector-block på ett element.
 * @param {HTMLElement} target
 * @param {HTMLElement|null} source Block-wrapper (.nitea-form-selector) eller null för standard
 */
function applyFormAppearanceFromBlock(target, source) {
    if (!target) {
        return;
    }

    target.classList.remove('form-color-scheme--light', 'form-color-scheme--dark');

    let colorScheme = 'dark';

    if (source) {
        const labelBg = source.getAttribute('data-label-background-color');
        if (labelBg) {
            target.setAttribute('data-label-background-color', labelBg);
        } else {
            target.removeAttribute('data-label-background-color');
        }

        if (source.classList.contains('form-color-scheme--light')) {
            colorScheme = 'light';
        } else if (source.classList.contains('form-color-scheme--dark')) {
            colorScheme = 'dark';
        } else {
            const schemeAttr = source.getAttribute('data-form-color-scheme');
            if (schemeAttr === 'light' || schemeAttr === 'dark') {
                colorScheme = schemeAttr;
            }
        }
    } else {
        target.setAttribute('data-label-background-color', 'default');
    }

    target.classList.add('form-color-scheme--' + colorScheme);
    target.setAttribute('data-form-color-scheme', colorScheme);
}

window.applyFormAppearanceFromBlock = applyFormAppearanceFromBlock;

/**
 * Global Modal Manager för Nitea Utils formulär
 * Hanterar öppning och stängning av modaler för alla formulärtyper
 */
class ModalManager {
    constructor() {
        this.modals = new Map();
        this.formTypes = new Map(); // Lagrar registrerade formulärtyper och deras komponenter
        this.setupGlobalListeners();
    }

    /**
     * Registrera en modal för en specifik formulärtyp
     * @param {string} formType - Formulärtyp (t.ex. 'general-contact')
     * @param {HTMLElement} modalElement - Modal-elementet
     */
    registerModal(formType, modalElement) {
        this.modals.set(formType, modalElement);
    }

    /**
     * Öppna en modal för en specifik formulärtyp
     * @param {string} formType - Formulärtyp
     * @param {Object} options - Alternativ för modalen
     */
    openModal(formType, options = {}) {
        const modal = this.modals.get(formType);
        if (!modal) {
            console.warn(`Modal for form type ${formType} not found`);
            return;
        }

        // Öppna modal genom att skicka event
        // Vue-komponenten hanterar resten genom sin event listener
        modal.dispatchEvent(new CustomEvent('openFormModal', {
            detail: {
                formType: formType,
                blockContainer: null, // Inga block-container attribut för manuella modaler
                ...options
            }
        }));
    }

    /**
     * Stäng en modal för en specifik formulärtyp
     * @param {string} formType - Formulärtyp
     */
    closeModal(formType) {
        const modal = this.modals.get(formType);
        if (!modal) {
            console.warn(`Modal for form type ${formType} not found`);
            return;
        }

        modal.dispatchEvent(new CustomEvent('closeFormModal', {
            detail: { formType }
        }));
    }

    /**
     * Sätt upp globala event-listeners
     */
    setupGlobalListeners() {
        // Lyssna på globala events för att öppna modaler
        window.addEventListener('openFormModal', (event) => {
            const { formType, blockContainer } = event.detail;
            
            // Hämta inställningar från block-containern om den finns
            const options = {};
            
            if (blockContainer) {
                options.emailRecipients = blockContainer.getAttribute('data-email-recipients');
                options.emailSubject = blockContainer.getAttribute('data-email-subject');
                options.formTitle = blockContainer.getAttribute('data-form-title');
                options.formDescription = blockContainer.getAttribute('data-form-description');
                options.postId = blockContainer.getAttribute('data-post-id');
                
                // För interest och test-drive: lägg till bilkonfigurationsdata
                if (formType === 'interest' || formType === 'test-drive') {
                    options.carBrand = blockContainer.getAttribute('data-car-brand');
                    options.carModel = blockContainer.getAttribute('data-car-model');
                    options.carPackage = blockContainer.getAttribute('data-car-package');
                    options.restrictFrontendModels = blockContainer.getAttribute('data-restrict-frontend-models') === 'true';
                    const allowedRaw = blockContainer.getAttribute('data-allowed-car-models');
                    if (allowedRaw) {
                        try {
                            const parsed = JSON.parse(allowedRaw);
                            options.allowedCarModelIds = Array.isArray(parsed) ? parsed.map(String) : [];
                        } catch (e) {
                            options.allowedCarModelIds = [];
                        }
                    } else {
                        options.allowedCarModelIds = [];
                    }
                }
            }

            this.openModal(formType, options);
        });

        // Lyssna på globala events för att stänga modaler
        window.addEventListener('closeFormModal', (event) => {
            const { formType } = event.detail;
            this.closeModal(formType);
        });

    }

    /**
     * Hämta alla registrerade modaler
     * @returns {Map} Map med alla registrerade modaler
     */
    getRegisteredModals() {
        return this.modals;
    }

    /**
     * Kontrollera om en modal är registrerad
     * @param {string} formType - Formulärtyp
     * @returns {boolean} True om modal är registrerad
     */
    isModalRegistered(formType) {
        return this.modals.has(formType);
    }

    /**
     * Kontrollera om en formulärtyp är registrerad
     * @param {string} formType - Formulärtyp
     * @returns {boolean} True om formulärtyp är registrerad
     */
    isFormTypeRegistered(formType) {
        return this.formTypes.has(formType);
    }

    /**
     * Registrera en formulärtyp med dess komponenter
     * @param {string} formType - Formulärtyp (t.ex. 'interest', 'test-drive')
     * @param {Object} components - Objekt med komponenter { formComponent, store, Vue }
     */
    registerFormType(formType, components) {
        // Lägg till formType till komponenterna för användning i ModalWrapper
        components.formType = formType;
        this.formTypes.set(formType, components);

        // Automatisk registrering av modal om den finns i DOM
        const modal = document.querySelector(`[data-form-type="${formType}"]`);
        if (modal) {
            this.registerModal(formType, modal);
        }
    }

    /**
     * Generisk metod för att skapa och montera ett formulär
     * @param {string} formType - Formulärtyp
     * @param {string|Element} selector - CSS-selektor eller DOM-element
     * @param {Object} props - Override properties
     * @returns {Vue|undefined} Monterad Vue-instans eller undefined vid fel
     */
    createFormApp(formType, selector, props = {}) {
        try {
            let element = null;

            if (selector instanceof Element) {
                element = selector;
            } else if (typeof selector === 'string') {
                const trimmedSelector = selector.trim();
                if (!trimmedSelector || trimmedSelector === '#' || trimmedSelector.includes('[object ')) {
                    console.warn('createFormApp: Ogiltig selektor:', selector);
                    return;
                }
                element = document.querySelector(trimmedSelector);
                if (!element) {
                    console.warn('createFormApp: Element hittades inte för selektor:', trimmedSelector);
                    return;
                }
            } else {
                console.warn('createFormApp: Selektor måste vara en sträng eller ett DOM-element');
                return;
            }

            // Hämta konfiguration
            const config = this.getFormConfig(element, props);

            // Skapa och returnera Vue-app
            return this.createVueApp(formType, config, element);

        } catch (error) {
            console.error('createFormApp: Misslyckades att skapa app:', error);
            return;
        }
    }

    /**
     * Skapar en Vue-instans baserat på konfiguration
     * @param {string} formType - Formulärtyp
     * @param {Object} config - Form-konfiguration
     * @param {HTMLElement} element - Element att montera i
     * @returns {Vue} Monterad Vue-instans
     * @throws {Error} Om konfiguration är ogiltig eller montering misslyckas
     */
    createVueApp(formType, config, element) {
        // Validera parametrar
        if (!config || typeof config !== 'object') {
            throw new Error('Ogiltigt konfigurationsobjekt');
        }

        if (!element || !(element instanceof Element)) {
            throw new Error('Giltigt DOM-element krävs för Vue-montering');
        }

        // Hämta komponenter för denna formulärtyp
        const components = this.formTypes.get(formType);
        if (!components) {
            throw new Error(`Formulärtyp '${formType}' är inte registrerad`);
        }

        // Validera display mode
        if (!['embedded', 'modal'].includes(config.displayMode)) {
            console.warn(`Ogiltigt display mode "${config.displayMode}", använder 'embedded'`);
            config.displayMode = 'embedded';
        }

        // Bygg gemensamma props
        const commonProps = {
            postId: Number(config.postId) || null,
            formTitle: String(config.formTitle || ''),
            formDescription: String(config.formDescription || ''),
            blockId: String(config.blockId || 'form'),
            emailRecipients: String(config.emailRecipients || ''),
            emailSubject: String(config.emailSubject || ''),
            facilityRecipients: Array.isArray(config.facilityRecipients) ? config.facilityRecipients : [],
            facilityFieldLabel: String(config.facilityFieldLabel || 'Välj anläggning'),
            facilityFieldRequired: Boolean(config.facilityFieldRequired),
            carBrand: String(config.carBrand || ''),
            carModel: String(config.carModel || ''),
            carPackage: String(config.carPackage || ''),
            restrictFrontendModels: Boolean(config.restrictFrontendModels),
            allowedCarModelIds: Array.isArray(config.allowedCarModelIds)
                ? config.allowedCarModelIds.map(String)
                : [],
        };

        try {
            let vueInstance;

            if (config.displayMode === 'modal') {
                // Modal-läge: Använd ModalWrapper för att hantera modal-logik
                if (!components.formComponent) {
                    throw new Error(`Form-komponent saknas för formulärtyp '${formType}'`);
                }

                // Skapa en modal wrapper komponent med formComponent
                const ModalComponent = createModalWrapperComponent(formType, components.formComponent);
                vueInstance = new components.Vue({
                    store: components.store,
                    render: h => h(ModalComponent, { props: commonProps })
                });
            } else {
                // Embedded-läge: Använd form-komponent direkt
                if (!components.formComponent) {
                    throw new Error(`Form-komponent saknas för formulärtyp '${formType}'`);
                }
                vueInstance = new components.Vue({
                    store: components.store,
                    render: h => h(components.formComponent, {
                        props: { ...commonProps, isModalOpen: false }
                    })
                });
            }

            return vueInstance.$mount(element);

        } catch (error) {
            console.error('Misslyckades att skapa Vue-app:', error);
            throw new Error(`Vue-app skapande misslyckades: ${error.message}`);
        }
    }

    /**
     * Extraherar form-konfiguration från DOM-element attribut och props
     * @param {HTMLElement|null} element - DOM-elementet att läsa attribut från
     * @param {Object} props - Override properties
     * @returns {Object} Komplett form-konfiguration
     */
    getFormConfig(element, props = {}) {
        const hasProps = Object.keys(props).length > 0;

        // Säker läsning av attribut med null-check
        const getAttribute = (attr) => element?.getAttribute(attr) || '';
        const getDataset = (key) => element?.dataset?.[key] || '';

        return {
            displayMode: props.displayMode || getAttribute('data-display-mode') || 'embedded',
            postId: props.postId || getDataset('postId') || getAttribute('data-post-id') || null,
            formTitle: props.formTitle || getDataset('formTitle') || getAttribute('data-form-title') || '',
            formDescription: props.formDescription || getDataset('formDescription') || getAttribute('data-form-description') || '',
            emailRecipients: props.emailRecipients || getAttribute('data-email-recipients') || '',
            emailSubject: props.emailSubject || getAttribute('data-email-subject') || '',
            blockId: props.blockId || element?.id || 'form',
            carBrand: props.carBrand || getAttribute('data-car-brand') || '',
            carModel: props.carModel || getAttribute('data-car-model') || '',
            carPackage: props.carPackage || getAttribute('data-car-package') || '',
            facilityRecipients: this.getFacilityRecipients(element, props, hasProps),
            facilityFieldLabel: props.facilityFieldLabel || getAttribute('data-facility-field-label') || 'Välj anläggning',
            facilityFieldRequired: props.facilityFieldRequired !== undefined
                ? props.facilityFieldRequired
                : (getAttribute('data-facility-field-required') === 'true'),
            tireType: props.tireType || getAttribute('data-tire-type') || '',
            rimsIncluded: props.rimsIncluded || getAttribute('data-rims-included') || '',
            restrictFrontendModels: typeof props.restrictFrontendModels === 'boolean'
                ? props.restrictFrontendModels
                : (getAttribute('data-restrict-frontend-models') === 'true'),
            allowedCarModelIds: (() => {
                if (Array.isArray(props.allowedCarModelIds)) {
                    return props.allowedCarModelIds.map(String);
                }
                const raw = getAttribute('data-allowed-car-models');
                if (!raw) {
                    return [];
                }
                try {
                    const parsed = JSON.parse(raw);
                    return Array.isArray(parsed) ? parsed.map(String) : [];
                } catch (error) {
                    console.warn('Misslyckades att parsa allowed car models JSON:', error.message);
                    return [];
                }
            })(),
        };
    }

    /**
     * Parsar facility recipients från element attribut eller props
     * @param {HTMLElement|null} element - DOM-elementet att läsa från
     * @param {Object} props - Props som kan innehålla facilityRecipients
     * @param {boolean} hasProps - Om props har skickats in
     * @returns {Array} Array av facility recipient objekt, eller tom array vid fel
     */
    getFacilityRecipients(element, props, hasProps) {
        // Börja med props om de finns
        let facilityRecipients = Array.isArray(props.facilityRecipients) ? props.facilityRecipients : [];

        // Om inga props eller tom array, försök läsa från element
        if (!hasProps || facilityRecipients.length === 0) {
            const facilityRecipientsData = element?.getAttribute('data-facility-recipients');
            if (facilityRecipientsData && facilityRecipientsData.trim()) {
                try {
                    const parsed = JSON.parse(facilityRecipientsData);
                    facilityRecipients = Array.isArray(parsed) ? parsed : [];
                } catch (error) {
                    console.warn('Misslyckades att parsa facility recipients JSON, använder tom array:', error.message);
                    facilityRecipients = [];
                }
            }
        }
        return facilityRecipients;
    }
}

// ====================
// MODAL WRAPPER COMPONENT
// ====================

/**
 * Skapar en Vue-komponent konfiguration för modal wrapper
 * Används av alla formulärtyper för att ersätta den duplicerade App.vue logiken
 */
function createModalWrapperComponent(formType, formComponent) {
    return {
        render(h) {
            return h('div', {
                attrs: {
                    id: 'form-' + formType,
                    'data-app': true
                },
                class: {
                    'nitea-modal': this.isModalOpen,
                    'nitea-modal--car-valuation': formType === 'car-valuation',
                    'is-open': this.isModalOpen,
                    'form-is-hidden': !this.isModalOpen
                },
                on: formType === 'car-valuation' ? {
                    click: (event) => {
                        if (event.target === event.currentTarget && this.isModalOpen) {
                            this.isModalOpen = false;
                            this.currentTargetFormId = null;
                        }
                    }
                } : {}
            }, [
                h(formComponent, {
                    props: {
                        postId: this.currentPostId,
                        isModalOpen: this.isModalOpen,
                        formTitle: this.currentFormTitle,
                        formDescription: this.currentFormDescription,
                        blockId: this.currentBlockId,
                        emailRecipients: this.emailRecipients,
                        emailSubject: this.emailSubject,
                        facilityRecipients: this.facilityRecipients,
                        facilityFieldLabel: this.facilityFieldLabel,
                        facilityFieldRequired: this.facilityFieldRequired,
                        carBrand: this.localCarBrand,
                        carModel: this.localCarModel,
                        carPackage: this.localCarPackage,
                        restrictFrontendModels: this.localRestrictFrontendModels,
                        allowedCarModelIds: this.localAllowedCarModelIds,
                        tireType: this.tireType,
                        rimsIncluded: this.rimsIncluded
                    }
                })
            ]);
        },
        props: {
            postId: { type: Number, default: null },
            formTitle: { type: String, default: '' },
            formDescription: { type: String, default: '' },
            blockId: { type: String, default: `form-${formType}` },
            carBrand: { type: String, default: '' },
            carModel: { type: String, default: '' },
            carPackage: { type: String, default: '' },
            restrictFrontendModels: { type: Boolean, default: false },
            allowedCarModelIds: { type: Array, default: () => [] },
            tireType: { type: String, default: '' },
            rimsIncluded: { type: String, default: '' },
        },
        data() {
            return {
                isModalOpen: false,
                currentTargetFormId: null,
                currentPostId: this.postId,
                currentFormTitle: this.formTitle,
                currentFormDescription: this.formDescription,
                currentBlockId: this.blockId,
                emailRecipients: '',
                emailRecipientsFromEvent: false,
                emailSubject: '',
                facilityRecipients: [],
                facilityFieldLabel: 'Välj anläggning',
                facilityFieldRequired: true,
                localCarBrand: this.carBrand,
                localCarModel: this.carModel,
                localCarPackage: this.carPackage,
                localRestrictFrontendModels: this.restrictFrontendModels,
                localAllowedCarModelIds: Array.isArray(this.allowedCarModelIds)
                    ? [...this.allowedCarModelIds.map(String)]
                    : [],
                localTireType: this.tireType,
                localRimsIncluded: this.rimsIncluded
            };
        },
        watch: {
            // Uppdatera lokala kopior när props ändras
            postId(newVal) { this.currentPostId = newVal; },
            formTitle(newVal) { this.currentFormTitle = newVal; },
            formDescription(newVal) { this.currentFormDescription = newVal; },
            blockId(newVal) { this.currentBlockId = newVal; },
            carBrand(newVal) { this.localCarBrand = newVal; },
            carModel(newVal) { this.localCarModel = newVal; },
            carPackage(newVal) { this.localCarPackage = newVal; },
            restrictFrontendModels(newVal) { this.localRestrictFrontendModels = newVal; },
            allowedCarModelIds: {
                handler(newVal) {
                    this.localAllowedCarModelIds = Array.isArray(newVal) ? [...newVal.map(String)] : [];
                },
                deep: true,
            },
            tireType(newVal) { this.localTireType = newVal; },
            rimsIncluded(newVal) { this.localRimsIncluded = newVal; },
            isModalOpen(isOpen) {
                if (isOpen) {
                    document.body.style.overflow = 'hidden';
                    document.body.classList.add('modal-open');
                    this.lockCarValuationPage();
                } else {
                    this.unlockCarValuationPage();
                    document.body.style.overflow = '';
                    document.body.classList.remove('modal-open');
                }
            },
        },
        mounted() {
            this.setupEventListeners();
            // Registrera denna modal med ModalManager
            if (window.modalManager) {
                window.modalManager.registerModal(formType, this.$el);
            }
        },
        beforeDestroy() {
            this.cleanupEventListeners();
            if (this.isModalOpen) {
                this.unlockCarValuationPage();
                document.body.style.overflow = '';
                document.body.classList.remove('modal-open');
            }
        },
        methods: {
            lockCarValuationPage() {
                if (formType !== 'car-valuation') {
                    return;
                }
                if (!window.matchMedia('(max-width: 1199px)').matches) {
                    return;
                }
                this._ncvScrollY = window.scrollY || window.pageYOffset || 0;
                document.documentElement.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.top = `-${this._ncvScrollY}px`;
                document.body.style.left = '0';
                document.body.style.right = '0';
                document.body.style.width = '100%';
            },
            unlockCarValuationPage() {
                if (formType !== 'car-valuation' || typeof this._ncvScrollY !== 'number') {
                    return;
                }
                const scrollY = this._ncvScrollY;
                this._ncvScrollY = null;
                document.documentElement.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.width = '';
                window.scrollTo(0, scrollY);
            },
            setupEventListeners() {
                this.openModalHandler = (event) => {
                    if (event.detail.formType === formType) {
                        const blockContainer = event.detail.blockContainer;

                        if (blockContainer) {
                            // Debug logging
                            // console.log('Modal öppnas med blockContainer:', blockContainer);
                            // console.log('data-car-brand:', blockContainer.getAttribute('data-car-brand'));
                            // console.log('data-car-model:', blockContainer.getAttribute('data-car-model'));
                            
                            // Uppdatera från block-inställningar
                            this.currentPostId = Number(blockContainer.getAttribute('data-post-id')) || null;
                            this.currentFormTitle = blockContainer.getAttribute('data-form-title') || '';
                            this.currentFormDescription = blockContainer.getAttribute('data-form-description') || '';
                            this.currentBlockId = blockContainer.getAttribute('data-form-id') || `form-${formType}`;

                            this.emailRecipients = blockContainer.getAttribute('data-email-recipients') || '';
                            this.emailRecipientsFromEvent = false;
                            this.emailSubject = blockContainer.getAttribute('data-email-subject') || '';

                            // Läs anläggningsdata
                            const facilityRecipientsData = blockContainer.getAttribute('data-facility-recipients');
                            if (facilityRecipientsData) {
                                try {
                                    this.facilityRecipients = JSON.parse(facilityRecipientsData);
                                } catch (e) {
                                    console.error('Error parsing facility recipients:', e);
                                    this.facilityRecipients = [];
                                }
                            }
                            this.facilityFieldLabel = blockContainer.getAttribute('data-facility-field-label') || 'Välj anläggning';
                            this.facilityFieldRequired = blockContainer.getAttribute('data-facility-field-required') === 'true';

                            // Läs bilkonfiguration
                            this.localCarBrand = blockContainer.getAttribute('data-car-brand') || '';
                            this.localCarModel = blockContainer.getAttribute('data-car-model') || '';
                            this.localCarPackage = blockContainer.getAttribute('data-car-package') || '';
                            this.localRestrictFrontendModels = blockContainer.getAttribute('data-restrict-frontend-models') === 'true';
                            const allowedModelsRaw = blockContainer.getAttribute('data-allowed-car-models');
                            if (allowedModelsRaw) {
                                try {
                                    const parsed = JSON.parse(allowedModelsRaw);
                                    this.localAllowedCarModelIds = Array.isArray(parsed) ? parsed.map(String) : [];
                                } catch (e) {
                                    this.localAllowedCarModelIds = [];
                                }
                            } else {
                                this.localAllowedCarModelIds = [];
                            }
                            // console.log('Satt localCarBrand till:', this.localCarBrand);
                            // console.log('Satt localCarModel till:', this.localCarModel);
                            
                            this.localTireType = blockContainer.getAttribute('data-tire-type') || '';
                            this.localRimsIncluded = blockContainer.getAttribute('data-rims-included') || '';

                            applyFormAppearanceFromBlock(this.$el, blockContainer);
                        } else {
                            // För manuella knappar, använd standardinställningar
                            this.currentPostId = this.postId;
                            this.currentFormTitle = this.formTitle;
                            this.currentFormDescription = this.formDescription;
                            this.currentBlockId = this.blockId;
                            this.localRestrictFrontendModels = this.restrictFrontendModels;
                            this.localAllowedCarModelIds = Array.isArray(this.allowedCarModelIds)
                                ? [...this.allowedCarModelIds.map(String)]
                                : [];

                            if (typeof event.detail.emailRecipients === 'string') {
                                this.emailRecipients = event.detail.emailRecipients;
                                this.emailRecipientsFromEvent = true;
                            } else if (this.emailRecipientsFromEvent) {
                                this.emailRecipients = '';
                                this.emailRecipientsFromEvent = false;
                            }

                            applyFormAppearanceFromBlock(this.$el, null);
                        }

                        this.isModalOpen = true;

                        if (formType === 'car-valuation') {
                            this.$nextTick(() => {
                                if (this.$el) {
                                    this.$el.scrollTop = 0;
                                }
                            });
                        }
                    }
                };

                this.closeModalHandler = (event) => {
                    if (event.detail.formType === formType) {
                        this.isModalOpen = false;
                        this.currentTargetFormId = null;
                    }
                };

                this.escapeKeyHandler = (event) => {
                    if (event.key === 'Escape' && this.isModalOpen) {
                        this.isModalOpen = false;
                        this.currentTargetFormId = null;
                    }
                };

                window.addEventListener('openFormModal', this.openModalHandler);
                window.addEventListener('closeFormModal', this.closeModalHandler);
                document.addEventListener('keydown', this.escapeKeyHandler);
            },
            cleanupEventListeners() {
                if (this.openModalHandler) {
                    window.removeEventListener('openFormModal', this.openModalHandler);
                }
                if (this.closeModalHandler) {
                    window.removeEventListener('closeFormModal', this.closeModalHandler);
                }
                if (this.escapeKeyHandler) {
                    document.removeEventListener('keydown', this.escapeKeyHandler);
                }
            }
        }
    };
}

// Gör createModalWrapperComponent tillgänglig globalt
window.createModalWrapperComponent = createModalWrapperComponent;

// Skapa global instans
window.modalManager = new ModalManager();

// Exportera för användning i moduler
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ModalManager, createModalWrapperComponent };
}
